import { api } from './api';

// Types based on the API documentation
export interface MonthlyPatientData {
  month: number;
  month_name: string;
  patient_count: number;
}

export interface PatientsPerMonthResponse {
  year: number;
  hospital_id: number;
  total_patients: number;
  monthly_data: MonthlyPatientData[];
  summary: {
    months_with_data: number;
    average_per_month: number;
  };
}

export interface ProcedureTypeStats {
  procedure_type_name: string;
  procedure_type_id: number;
  count: number;
  complications_count: number;
  survival_rate: number;
}

export interface MonthlyTrendData {
  month_year: string;
  month_name: string;
  year: number;
  month: number;
  procedure_count: number;
  surgery_count: number;
  catheterization_count: number;
}

export interface TopProcedureData {
  procedure_name: string;
  procedure_code: string;
  count: number;
  complications_count: number;
  complication_rate: number;
}

export interface ProcedureStatisticsResponse {
  hospital_id: number;
  filters: {
    year?: string;
    start_date?: string;
    end_date?: string;
  };
  metrics: {
    total_procedures: number;
    unique_patients: number;
    surgeries: number;
    catheterizations: number;
    procedures_with_complications: number;
    procedures_with_notes: number;
    mortality_30d: number;
    reinterventions_1y: number;
    rates: {
      surgery_rate: number;
      catheterization_rate: number;
      complication_rate: number;
      mortality_rate_30d: number;
      reintervention_rate_1y: number;
    };
  };
  procedure_types: ProcedureTypeStats[];
  monthly_trend: MonthlyTrendData[];
  top_procedures: TopProcedureData[];
}

export interface DashboardStatisticsResponse {
  patients: {
    total: number;
    this_year: number;
    last_30_days: number;
    female: number;
    male: number;
    severe_chd_cases: number;
    active_chd_cases: number;
    premature_cases: number;
    average_age: number;
  };
  procedures: {
    total: number;
    this_year: number;
    last_30_days: number;
    surgeries: number;
    catheterizations: number;
  };
  diagnoses: {
    patients_with_diagnoses: number;
    total_diagnoses: number;
    primary_diagnoses: number;
    unique_icd10_codes: number;
  };
  metadata: {
    hospital_id: number;
    current_year: number;
    generated_at: string;
  };
}

export class StatisticsService {
  /**
   * Get patients per month statistics
   */
  async getPatientsPerMonth(year?: number): Promise<PatientsPerMonthResponse> {
    const params: any = {};
    if (year) {
      params.year = year.toString();
    }

    const response = await api.get<PatientsPerMonthResponse>('/api/statistics/patients-per-month', {
      params
    });

    if (!response.data) {
      throw new Error('Nu s-au putut încărca datele pentru pacienți');
    }

    return response.data;
  }

  /**
   * Get comprehensive procedure statistics
   */
  async getProcedureStatistics(filters?: {
    year?: number;
    start_date?: string;
    end_date?: string;
  }): Promise<ProcedureStatisticsResponse> {
    const params: Record<string, string> = {};

    if (filters?.year) {
      params.year = filters.year.toString();
    }
    if (filters?.start_date) {
      params.start_date = filters.start_date;
    }
    if (filters?.end_date) {
      params.end_date = filters.end_date;
    }

    const response = await api.get<ProcedureStatisticsResponse>('/api/statistics/procedures', {
      params
    });

    if (!response.data) {
      throw new Error('Nu s-au putut încărca datele pentru proceduri');
    }

    return response.data;
  }

  /**
   * Get dashboard overview statistics
   * @param section Optional registry section ('chd' or 'prematuri') to scope patient counts to
   * that section only, excluding patients that only exist in the other section.
   */
  async getDashboardStatistics(section?: 'chd' | 'prematuri'): Promise<DashboardStatisticsResponse> {
    const response = await api.get<DashboardStatisticsResponse>('/api/statistics/dashboard', {
      params: section ? { section } : undefined,
    });

    if (!response.data) {
      throw new Error('Nu s-au putut încărca statisticile generale');
    }

    return response.data;
  }
}

// Export singleton instance
export const statisticsService = new StatisticsService();